import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { getProfile } from '@/service/user/profile'
import { getMyUserPermission } from '@/service/userPermission/userPermission'
import { pageNames } from '@/enums/pageNames'
import { userPermissionStore } from '@/stores/user/permission'
import type { iUserProfile } from '@/stores/user/userProfile'
import { userProfileStore } from '@/stores/user/userProfile'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // HOME
    {
      path: '/',
      name: pageNames.HOME,
      component: HomeView
    },
    // TOOLS
    {
      path: '/subscription',
      children: [
        {
          path: 'master-subscription',
          name: pageNames.SUBSCRIPTION,
          component: () =>
            import('@/views/subscriptions/masterSubscription/masterSubscriptionView.vue')
        },
        {
          path: 'master-subscription/create',
          name: pageNames.SUBSCRIPTIONCREATE,
          component: () =>
            import('@/views/subscriptions/masterSubscription/masterSubscriptionCreateView.vue')
        },
        {
          path: 'master-subscription/detail/:id',
          name: pageNames.SUBSCRIPTIONDETAIL,
          component: () =>
            import('@/views/subscriptions/masterSubscription/masterSubscriptionDetailView.vue')
        }
      ]
    },
    {
      path: '/tool',
      children: [
        {
          path: 'content-slider',
          name: pageNames.HYBRIDSLIDER,
          component: () => import('@/views/tools/hybridSlider/hybridSliderView.vue')
        },
        {
          path: 'content-slider/create',
          name: pageNames.HYBRIDSLIDERCREATE,
          component: () => import('@/views/tools/hybridSlider/hybridSliderCreateView.vue')
        },
        {
          path: 'content-slider/detail/:id',
          name: pageNames.HYBRIDSLIDERDETAIL,
          component: () => import('@/views/tools/hybridSlider/hybridSliderDetailView.vue')
        },
        {
          path: 'text-slider',
          name: pageNames.TEXTSLIDER,
          component: () => import('@/views/tools/textSlider/textSliderView.vue')
        },
        {
          path: 'text-slider/create',
          name: pageNames.TEXTSLIDERCREATE,
          component: () => import('@/views/tools/textSlider/textSliderCreateView.vue')
        },
        {
          path: 'text-slider/detail/:id',
          name: pageNames.TEXTSLIDERDETAIL,
          component: () => import('@/views/tools/textSlider/textSliderDetailView.vue')
        },
        {
          path: 'stocks',
          name: pageNames.STOCKS,
          component: () => import('@/views/tools/stocks/stockView.vue')
        },
        {
          path: 'account-wall',
          name: pageNames.ACCOUNT_WALL,
          component: () => import('@/views/tools/accountWall/accountWallView.vue')
        },
        {
          path: 'legal-view',
          name: pageNames.LEGAL,
          component: () => import('@/views/tools/legal/legalView.vue')
        },
        {
          path: 'legal-view/circle-disclosure',
          name: pageNames.LEGALCIRCLEDISCLOSURE,
          component: () => import('@/views/tools/legal/circleDisclosureDetailView.vue')
        },
        {
          path: 'legal-view/circle-disclosure/new',
          name: pageNames.LEGALCIRCLEDISCLOSURENEW,
          component: () => import('@/views/tools/legal/circleDisclosureCreateView.vue')
        },
        {
          path: 'legal-view/information',
          name: pageNames.LEGALINFORMATION,
          component: () => import('@/views/tools/legal/LegalInformationView.vue')
        },
        {
          path: 'legal-view/information/editor',
          name: pageNames.LEGALINFORMATIONEDITOR,
          component: () => import('@/views/tools/legal/LegalInformationEditorView.vue')
        }
      ]
    },
    // AUTH
    {
      path: '/signin',
      name: pageNames.LOGIN,
      component: () => import('@/views/LoginView.vue')
    },
    {
      path: '/forgot-password',
      name: pageNames.FORGOTPASSWORD,
      component: () => import('@/views/ForgotPasswordView.vue')
    },
    {
      path: '/reset-password',
      name: pageNames.RESETPASSWORD,
      component: () => import('@/views/ResetPasswordView.vue')
    },
    {
      path: '/signout',
      name: pageNames.LOGOUT,
      component: () => import('@/views/LogoutView.vue')
    },

    // USER PROFILE
    {
      path: '/user',
      name: pageNames.USERPROFILE,
      component: () => import('@/views/user/UserProfileView.vue')
    },
    {
      path: '/client/list',
      name: pageNames.CLIENT,
      component: () => import('@/views/client/ClientLandingView.vue')
    },

    // INTERNAL USER
    {
      path: '/internal-user/editor/existing',
      redirect: { name: pageNames.USER }
    },
    {
      path: '/internal-user/editor',
      redirect: { name: pageNames.USER }
    },
    {
      path: '/internal-user',
      name: pageNames.USER,
      component: () => import('@/views/internalUser/UserLandingView.vue')
    },
    {
      path: '/internal-user/new',
      name: pageNames.USEREDITORNEW,
      component: () => import('@/views/internalUser/NewUserLandingView.vue')
    },
    {
      path: '/internal-user/editor/existing/:id',
      name: pageNames.USEREDITOREXISTING,
      component: () => import('@/views/internalUser/UserEditorView.vue')
    },
    // ALGONEWS
    {
      path: '/algo-news/editor/existing',
      redirect: { name: pageNames.ALGONEWS }
    },
    {
      path: '/algo-news/editor',
      redirect: { name: pageNames.ALGONEWS }
    },
    {
      path: '/algo-news',
      name: pageNames.ALGONEWS,
      component: () => import('@/views/algoNews/AlgoNewsLandingView.vue')
    },
    {
      path: '/algo-news/new',
      name: pageNames.ALGONEWSEDITORNEW,
      component: () => import('@/views/algoNews/NewAlgoNewsEditorView.vue')
    },
    {
      path: '/algo-news/editor/existing/:id',
      name: pageNames.ALGONEWSEDITOREXISTING,
      component: () => import('@/views/algoNews/AlgoNewsEditorView.vue')
    },
    // ad banner
    {
      path: '/ad-banner/existing',
      redirect: { name: pageNames.ADBANNER }
    },
    {
      path: '/ad-banner/editor',
      redirect: { name: pageNames.ADBANNER }
    },
    {
      path: '/ad-banner',
      name: pageNames.ADBANNER,
      component: () => import('@/views/adBanner/adBannerLandingView.vue')
    },
    {
      path: '/ad-banner/new',
      name: pageNames.ADBANNEREDITORNEW,
      component: () => import('@/views/adBanner/NewAdBannerLandingView.vue')
    },
    {
      path: '/ad-banner/editor/existing/:id',
      name: pageNames.ADBANNEREDITOREXISTING,
      component: () => import('@/views/adBanner/adBannerEditorView.vue')
    },
    // INSTITUTION
    {
      path: '/institution/editor/existing',
      redirect: { name: pageNames.INSTITUTION }
    },
    {
      path: '/institution/editor',
      redirect: { name: pageNames.INSTITUTION }
    },
    {
      path: '/institution',
      name: pageNames.INSTITUTION,
      component: () => import('@/views/institution/InstitutionLandingView.vue')
    },
    {
      path: '/institution/new',
      name: pageNames.INSTITUTIONEDITORNEW,
      component: () => import('@/views/institution/NewInstitutionLandingView.vue')
    },
    {
      path: '/institution-stock-placement/list',
      name: pageNames.INSTITUTIONSTOCKPLACEMENT,
      component: () => import('@/views/institutionStockPlacement/institutionStockPlacementLandingView.vue')
    },
    {
      path: '/institution-stock-placement/detail/:id',
      name: pageNames.INSTITUTIONSTOCKPLACEMENTDETAIL,
      component: () => import('@/views/institutionStockPlacement/institutionStockPlacementEditorView.vue')
    },
    {
      path: '/institution/editor/existing/:id',
      name: pageNames.INSTITUTIONEDITOREXISTING,
      component: () => import('@/views/institution/InstitutionEditorView.vue')
    },
    // STREETCALL CONTENT
    {
      path: '/streetcall-content/editor/existing',
      redirect: { name: pageNames.STREETCALLCONTENT }
    },
    {
      path: '/streetcall-content/editor',
      redirect: { name: pageNames.STREETCALLCONTENT }
    },
    {
      path: '/streetcall-content',
      name: pageNames.STREETCALLCONTENT,
      component: () => import('@/views/streetcallContent/StreetcallContentLandingView.vue')
    },
    {
      path: '/streetcall-content/new',
      name: pageNames.STREETCALLCONTENTEDITORNEW,
      component: () => import('@/views/streetcallContent/NewStreetcallContentEditorView.vue')
    },
    {
      path: '/streetcall-content/editor/existing/:id',
      name: pageNames.STREETCALLCONTENTEDITOREXISTING,
      component: () => import('@/views/streetcallContent/StreetcallContentEditorView.vue')
    },
    // FFS CONTENT
    {
      path: '/ffs-content/editor/existing',
      redirect: { name: pageNames.FFSCONTENT }
    },
    {
      path: '/ffs-content/editor',
      redirect: { name: pageNames.FFSCONTENT }
    },
    {
      path: '/ffs-content',
      name: pageNames.FFSCONTENT,
      component: () => import('@/views/ffsContent/FfsContentLandingView.vue')
    },
    {
      path: '/ffs-content/new',
      name: pageNames.FFSCONTENTEDITORNEW,
      component: () => import('@/views/ffsContent/NewFfsContentEditorView.vue')
    },
    {
      path: '/ffs-content/editor/existing/:id',
      name: pageNames.FFSCONTENTEDITOREXISTING,
      component: () => import('@/views/ffsContent/FfsContentEditorView.vue')
    },
    // INSTITUTION CONTENT
    {
      path: '/institution-content/editor/existing',
      redirect: { name: pageNames.INSTITUTIONCONTENT }
    },
    {
      path: '/institution-content/editor',
      redirect: { name: pageNames.INSTITUTIONCONTENT }
    },
    {
      path: '/institution-content',
      name: pageNames.INSTITUTIONCONTENT,
      component: () => import('@/views/institutionContent/InstitutionContentLandingView.vue')
    },
    {
      path: '/institution-content/new',
      name: pageNames.INSTITUTIONCONTENTEDITORNEW,
      component: () => import('@/views/institutionContent/NewInstitutionContentEditorView.vue')
    },
    {
      path: '/institution-content/editor/existing/:id',
      name: pageNames.INSTITUTIONCONTENTEDITOREXISTING,
      component: () => import('@/views/institutionContent/InstitutionContentEditorView.vue')
    },

    // PERMISSION
    // {
    //   path: '/permission',
    //   name: pageNames.PERMISSION,
    //   component: () => import('@/views/internalUser/PermissionLandingViewDONTUSE.vue')
    // },
    {
      path: '/permission/editor/existing',
      redirect: { name: pageNames.USER }
    },
    {
      path: '/permission/editor',
      redirect: { name: pageNames.USER }
    },
    {
      path: '/permission',
      redirect: { name: pageNames.USER }
    },
    {
      path: '/permission/editor/existing/:id',
      name: pageNames.PERMISSIONEDITOREXISTING,
      component: () => import('@/views/internalUser/PermissionEditorView.vue')
    },

    // COMMUNITY
    {
      path: '/contributor/editor/existing',
      redirect: { name: pageNames.CONTRIBUTOR }
    },
    {
      path: '/contributor/editor',
      redirect: { name: pageNames.CONTRIBUTOR }
    },
    {
      path: '/contributor',
      name: pageNames.CONTRIBUTOR,
      component: () => import('@/views/community/ContributorLandingView.vue')
    },
    {
      path: '/contributor/new',
      name: pageNames.CONTRIBUTOREDITORNEW,
      component: () => import('@/views/community/NewContributorLandingView.vue')
    },
    {
      path: '/contributor/editor/existing/:id',
      name: pageNames.CONTRIBUTOREDITOREXISTING,
      component: () => import('@/views/community/ContributorEditorView.vue')
    },
    {
      path: '/circle-research-content/landing',
      name: pageNames.COMMUNITYRESEARCHCONTENT,
      component: () => import('@/views/community/ResearchContentLandingView.vue')
    },
    {
      path: '/circle-research-content/editor/existing/:id',
      name: pageNames.COMMUNITYRESEARCHEDITOREXISTING,
      component: () => import('@/views/community/ResearchContentEditorView.vue')
    },
    // SUB ACCOUNT CONTRIBUTOR
    {
      path: '/contributor/editor/existing/sub-account/new/:id',
      name: pageNames.SUBACCOUNTCONTRIBUTOREDITORNEW,
      component: () => import('@/views/community/subAccount/NewSubAccountContributorLandingView.vue')
    },
    {
      path: '/contributor/editor/existing/sub-account/existing/:id',
      name: pageNames.SUBACCOUNTCONTRIBUTOREDITOREXISTING,
      component: () => import('@/views/community/subAccount/SubAccountContributorEditorView.vue')
    },

    // RESEARCH
    // {
    //   path: '/research',
    //   redirect: { name: pageNames.RESEARCH }
    // },
    // {
    //   path: '/research/editor',
    //   redirect: { name: pageNames.RESEARCH }
    // },
    // {
    //   path: '/research/editor/existing',
    //   redirect: { name: pageNames.RESEARCH }
    // },
    // {
    //   path: '/research/landing',
    //   name: pageNames.RESEARCH,
    //   component: () => import('../views/research/ResearchLandingView.vue')
    // },
    // {
    //   path: '/research/editor/new',
    //   name: pageNames.RESEARCHEDITORNEW,
    //   component: () => import('../views/research/NewResearchEditorView.vue')
    // },
    // {
    //   path: '/research/editor/existing/:id',
    //   name: pageNames.RESEARCHEDITOREXISTING,
    //   component: () => import('@/views/research/ResearchEditorView.vue')
    // },
    // RESEARCH -> ALGO RESEARCH
    {
      path: '/algo-research/editor/existing',
      redirect: { name: pageNames.ALGORESEARCH }
    },
    {
      path: '/algo-research/editor',
      redirect: { name: pageNames.ALGORESEARCH }
    },
    {
      path: '/algo-research',
      name: pageNames.ALGORESEARCH,
      component: () => import('@/views/research/algo/AlgoResearchLandingView.vue')
    },
    {
      path: '/algo-research/new',
      name: pageNames.ALGORESEARCHEDITORNEW,
      component: () => import('@/views/research/algo/NewAlgoResearchEditorView.vue')
    },
    {
      path: '/algo-research/editor/existing/:id',
      name: pageNames.ALGORESEARCHEDITOREXISTING,
      component: () => import('@/views/research/algo/AlgoResearchEditorView.vue')
    },
    // RESEARCH -> NEWS LETTER
    {
      path: '/newsletter',
      name: pageNames.NEWSLETTER,
      component: () => import('@/views/research/newsLetter/NewsLetterLandingView.vue')
    },
    {
      path: '/newsletter/update',
      name: pageNames.NEWSLETTEREDITORUPDATE,
      component: () => import('@/views/research/newsLetter/UpdateNewsletterEditorView.vue')
    },
    {
      path: '/newsletter/detail/existing/:id',
      name: pageNames.NEWSLETTERDETAILEXISTING,
      component: () => import('@/views/research/newsLetter/NewsLetterDetailView.vue')
    },
    // RESEARCH -> RESEARCH BLAST
    {
      path: '/research-blast/editor/existing',
      redirect: { name: pageNames.RESEARCHBLAST }
    },
    {
      path: '/research-blast/editor',
      redirect: { name: pageNames.RESEARCHBLAST }
    },
    {
      path: '/research-blast',
      name: pageNames.RESEARCHBLAST,
      component: () => import('@/views/research/researchBlast/ResearchBlastLandingView.vue')
    },
    {
      path: '/research-blast/new',
      name: pageNames.RESEARCHBLASTNEW,
      component: () => import('@/views/research/researchBlast/NewResearchBlastEditorView.vue')
    },
    {
      path: '/research-blast/editor/existing/:id',
      name: pageNames.RESEARCHBLASTEDITOREXISTING,
      component: () => import('@/views/research/researchBlast/ResearchBlastEditorView.vue')
    },
    // RESEARCH -> CATEGORY
    {
      path: '/category/editor/existing',
      redirect: { name: pageNames.CATEGORY }
    },
    {
      path: '/category/editor',
      redirect: { name: pageNames.CATEGORY }
    },
    {
      path: '/category',
      name: pageNames.CATEGORY,
      component: () => import('@/views/research/category/CategoryLandingView.vue')
    },
    {
      path: '/category/new',
      name: pageNames.CATEGORYEDITORNEW,
      component: () => import('@/views/research/category/NewCategoryEditorView.vue')
    },
    {
      path: '/category/editor/existing/:id',
      name: pageNames.CATEGORYEDITOREXISTING,
      component: () => import('@/views/research/category/CategoryEditorView.vue')
    },

    // RESEARCH -> MASTER TAG
    {
      path: '/mastertag',
      name: pageNames.MASTERTAG,
      component: () => import('@/views/research/masterTag/MasterTagLandingView.vue')
    },
    {
      path: '/mastertag/new',
      name: pageNames.MASTERTAGNEW,
      component: () => import('@/views/research/masterTag/NewMasterTagEditorView.vue')
    }
  ]
})

router.beforeEach(async (to, from) => {
  const userPermission = userPermissionStore()
  const userProfile = userProfileStore()
  if (
    to.name !== pageNames.LOGIN &&
    to.name !== pageNames.LOGOUT &&
    to.name !== pageNames.FORGOTPASSWORD &&
    to.name !== pageNames.RESETPASSWORD
  ) {
    if (userProfile.getUserProfile.fullname === '') {
      const res = await getProfile()
      const fetchResult = res?.data?.data as iUserProfile | undefined
      if (fetchResult) {
        userProfile.setUserProfile(fetchResult)
      } else {
        userProfile.setUserProfile({ fullname: '' } as iUserProfile)
      }
    }
    const permRes = await getMyUserPermission()
    const permission = permRes?.data?.data
    if (permission !== null && permission !== undefined) {
      userPermission.setUserProfile(permission)
    } else {
      userPermission.setDefault()
    }
    userPermission.setFetchFlag(true)
  }
})

export default router
